/* ==========================================================================
   ZONE BLEUE — catalogue engine (vanilla JS, no dependencies)
   Inquiry list + WhatsApp ordering, header, search, rails, collection filters,
   product page, colour studio, live opening hours. RTL-aware throughout.
   ========================================================================== */
(() => {
  'use strict';

  const cfgEl = document.getElementById('zb-config');
  if (!cfgEl) return;
  const cfg = JSON.parse(cfgEl.textContent);
  const T = cfg.t || {};
  const RTL = cfg.dir === 'rtl';
  const ROOT = (cfg.root || '/').replace(/\/$/, '');
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const fill = (str, map) => Object.keys(map).reduce((s, k) => s.split('[' + k + ']').join(map[k]), str || '');
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const absUrl = (u) => (/^https?:/.test(u) ? u : (cfg.shopUrl || location.origin) + u);
  const refOf = (title) => { const m = String(title).match(/(\d{3,})(?!.*\d)/); return m ? parseInt(m[1], 10) : 0; };

  /* ---------- colours ---------- */
  const ckey = (v) => String(v || '').toLowerCase().replace(/[\s\-_/]+/g, '');
  const colorOf = (v) => {
    const k = ckey(v);
    if (cfg.colors && cfg.colors[k]) return cfg.colors[k];
    if (window.CSS && CSS.supports && CSS.supports('color', k)) return k;
    return null;
  };

  // 'RoyalBlue' -> 'Royal Blue' for display only (data values stay untouched)
  const pretty = (v) => String(v || '').replace(/([a-z])([A-Z])/g, '$1 $2');

  /* ---------- WhatsApp ---------- */
  const waUrl = (text) => 'https://wa.me/' + cfg.wa + '?text=' + encodeURIComponent(text);
  const openWa = (text, anchor) => {
    const url = waUrl(text);
    if (anchor && anchor.tagName === 'A') { anchor.href = url; anchor.target = '_blank'; anchor.rel = 'noopener'; return true; }
    window.open(url, '_blank', 'noopener');
    return false;
  };
  const productMessage = ({ title, color, size, qty, url }) => {
    const lines = [T.waHello, T.waProduct, '', '• ' + title];
    if (color) lines.push(T.waColour + ': ' + color);
    if (size) lines.push(T.waSizes + ': ' + size);
    if (qty) lines.push(T.waQty + ': ' + qty);
    lines.push(T.waLink + ': ' + absUrl(url), '', T.waThanks);
    return lines.join('\n');
  };

  /* ==========================================================================
     WHOLESALE RULES (client rules, 25 Sep 2026)
     - Styles with sizes: every colour is a full series (1 piece of each size);
       the quantity is a number of series. No split series on the website —
       splits are negotiated on WhatsApp.
     - One-size styles: all colours of the style, minimum 1 piece per colour.
     - Black is never ordered alone (needs another colour of the same style).
       Black can be above the other colours only when all colours are taken,
       and then at most double.
     ========================================================================== */
  const isBlack = (v) => ['black', 'أسود', 'اسود', 'noir'].includes(ckey(v));
  const activeKeys = (it) => Object.keys(it.lines).filter((k) => it.lines[k] > 0 && (!it.colors.length || it.colors.includes(k)));
  const Rules = {
    unit: (it) => (it.one ? T.unitPcs : T.unitSeries),
    units: (it) => activeKeys(it).reduce((a, k) => a + it.lines[k], 0),
    pieces: (it) => Rules.units(it) * (it.one ? 1 : (it.per || 1)),
    check(it) {
      const colors = it.colors || [];
      const chosen = activeKeys(it);
      if (!chosen.length) return { ok: false, msg: T.errEmpty };
      if (it.one && colors.length > 1 && colors.some((c) => !(it.lines[c] > 0))) return { ok: false, msg: fill(T.errOneSize, { n: colors.length }), fix: true };
      const black = chosen.find(isBlack);
      const others = colors.filter((c) => !isBlack(c));
      if (black && others.length) {
        const otherQty = chosen.filter((c) => !isBlack(c)).map((c) => it.lines[c]);
        if (!otherQty.length) return { ok: false, msg: T.errBlackAlone };
        const max = Math.max.apply(null, otherQty);
        const allTaken = others.every((c) => it.lines[c] > 0);
        if (it.lines[black] > max && !allTaken) return { ok: false, msg: T.errBlackMore };
        if (it.lines[black] > max * 2) return { ok: false, msg: T.errBlackDouble };
      }
      if (it.one) {
        const q = others.map((c) => it.lines[c]).filter(Boolean);
        if (q.some((x) => x !== q[0])) return { ok: false, msg: fill(T.errOneSize, { n: colors.length }), fix: true };
      }
      return { ok: true };
    },
    // One-size styles (client rule, 25 Sep 2026): every non-black colour carries the
    // same quantity N; black carries N..2N (up to double). Changing one colour moves the others:
    // e.g. rest 4 -> black 4..8 with no change; black 9 -> the rest become 5; black 3 (rest 1) -> the rest become 2.
    normalize(it, changed) {
      if (!it.one || !it.colors.length) return it;
      const black = it.colors.find(isBlack);
      const others = it.colors.filter((c) => !isBlack(c));
      if (!others.length) { it.colors.forEach((c) => { it.lines[c] = Math.max(1, it.lines[c] || 0); }); return it; }
      const cur = others.map((c) => it.lines[c] || 0);
      let n = Math.max(1, ...cur);
      if (changed && !isBlack(changed)) n = Math.max(1, it.lines[changed] || 0);
      if (changed && black && isBlack(changed)) {
        const b = Math.max(1, it.lines[black] || 0);
        n = Math.max(1, cur.find((q) => q > 0) || 1);
        if (b > 2 * n) n = Math.ceil(b / 2); // past double: the other colours go up just enough
        if (b < n) n = b;
      }
      others.forEach((c) => { it.lines[c] = n; });
      if (black) it.lines[black] = Math.min(2 * n, Math.max(n, it.lines[black] || n));
      return it;
    },
    // Starting selection when a style is first added
    defaults(p) {
      const lines = {};
      if (p.one) (p.colors || []).forEach((c) => { lines[c] = 1; });
      else if ((p.colors || []).length === 1) lines[p.colors[0]] = 1;
      return lines;
    }
  };

  const styleLines = (it, indent) => {
    const pad = indent ? '   ' : '';
    const out = [];
    (it.colors.length ? it.colors : Object.keys(it.lines)).forEach((c) => { if (it.lines[c] > 0) out.push(pad + pretty(c) + ' × ' + it.lines[c] + ' ' + Rules.unit(it)); });
    if (!it.one && it.sizes.length) out.push(pad + T.fullSeries + ': ' + it.sizes.join(' / '));
    out.push(pad + T.total + ': ' + Rules.pieces(it) + ' ' + T.unitPcs);
    return out;
  };

  /* ==========================================================================
     INQUIRY LIST STORE — one entry per style, colour → quantity
     ========================================================================== */
  const KEY = 'zb-inquiry-v2';
  const META = 'zb-inquiry-meta';
  const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) || d; } catch (e) { return d; } };
  const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } };
  try { localStorage.removeItem('zb-inquiry-v1'); } catch (e) { /* noop */ }

  const relang = (it, p) => {
    if (!it || !p || !p.colors || JSON.stringify(it.colors) === JSON.stringify(p.colors)) return it;
    const lines = {};
    Object.keys(it.lines).forEach((k) => { const i = it.colors.indexOf(k); lines[i >= 0 && p.colors[i] != null ? p.colors[i] : k] = it.lines[k]; });
    Object.assign(it, { lines, colors: p.colors, sizes: p.sizes || [], title: p.title, url: p.url });
    return it;
  };

  const List = {
    items: read(KEY, []).filter((i) => i && i.lines),
    meta: read(META, { name: '', note: '' }),
    save() { write(KEY, this.items); render(); },
    get(id) { return this.items.find((i) => String(i.id) === String(id)); },
    fromProduct(p) { return { id: p.id, title: p.title, url: p.url, image: p.image, colors: p.colors || [], sizes: p.sizes || [], one: !!p.one, per: p.per || 1, lines: {} }; },
    // replace = true: the product page sends its full selection
    put(p, lines, opts = {}) {
      let it = relang(this.get(p.id), p);
      if (!it) { it = this.fromProduct(p); this.items.push(it); }
      if (opts.replace) it.lines = {};
      Object.keys(lines).forEach((c) => { if (lines[c] > 0) it.lines[c] = opts.replace ? lines[c] : (it.lines[c] || 0) + lines[c]; });
      if (opts.image) it.image = opts.image;
      this.save();
      bumpCount();
      return it;
    },
    remove(id) { this.items = this.items.filter((i) => String(i.id) !== String(id)); this.save(); },
    clear() { this.items = []; this.save(); },
    has(id) { return !!this.get(id); },
    count() { return this.items.length; },
    valid() { return this.items.length > 0 && this.items.every((i) => Rules.check(i).ok); },
    message() {
      const lines = [T.waHello, T.waList, ''];
      this.items.forEach((it, n) => {
        lines.push((n + 1) + '. ' + it.title);
        lines.push.apply(lines, styleLines(it, true));
        lines.push('   ' + absUrl(it.url), '');
      });
      if (this.meta.name) lines.push(T.waName + ': ' + this.meta.name);
      if (this.meta.note) lines.push(T.waNote + ': ' + this.meta.note);
      lines.push(T.waThanks);
      return lines.join('\n');
    }
  };

  /* ---------- order builder (shared by product page + list) ---------- */
  const xIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  function builderHTML(it, scope, withPicker) {
    const res = Rules.check(it);
    const order = it.colors.length ? it.colors : Object.keys(it.lines);
    const rows = order.filter((c) => it.lines[c] > 0).map((c) =>
      '<div class="zb-bld__row" data-c="' + esc(c) + '">' +
        '<span class="zb-swatch" style="--c:' + esc(colorOf(c) || 'transparent') + '"></span>' +
        '<span class="zb-bld__name">' + esc(pretty(c)) + '</span>' +
        '<span class="zb-qty zb-qty--sm"><button type="button" data-zb-bq="-1" aria-label="-">−</button><input type="number" min="0" inputmode="numeric" value="' + it.lines[c] + '" data-zb-bq-input aria-label="' + esc(T.quantity) + '"><button type="button" data-zb-bq="1" aria-label="+">+</button></span>' +
        '<span class="zb-bld__unit">' + esc(Rules.unit(it)) + '</span>' +
        (it.one ? '<span class="zb-bld__del"></span>' : '<button type="button" class="zb-bld__del" data-zb-bdel aria-label="' + esc(T.remove) + '">' + xIcon + '</button>') +
      '</div>').join('');
    const missing = withPicker ? it.colors.filter((c) => !(it.lines[c] > 0)) : [];
    const picker = missing.length
      ? '<div class="zb-bld__add"><span>' + esc(T.addColour) + '</span>' + missing.map((c) => '<button type="button" class="zb-swatch" style="--c:' + esc(colorOf(c) || 'transparent') + '" data-zb-badd="' + esc(c) + '" title="' + esc(pretty(c)) + '" aria-label="' + esc(pretty(c)) + '"></button>').join('') + '</div>'
      : '';
    const n = activeKeys(it).length;
    const summary = n
      ? '<div class="zb-bld__sum"><span>' + esc(T.colours) + ': ' + n + (it.one ? '' : ' · ' + Rules.units(it) + ' ' + esc(Rules.unit(it))) + '</span><b>' + esc(T.total) + ': ' + Rules.pieces(it) + ' ' + esc(T.unitPcs) + '</b></div>'
      : '';
    const rule = res.ok ? '' : '<p class="zb-rule" role="alert">' + esc(res.msg) + (res.fix ? ' <button type="button" data-zb-bfix>' + esc(T.fixAll) + '</button>' : '') + '</p>';
    return '<div class="zb-bld' + (res.ok ? '' : ' is-invalid') + '" data-scope="' + scope + '" data-id="' + esc(it.id) + '">' + rows + picker + summary + rule + '</div>';
  }

  let pdp = null; // { it, render } on product pages
  const scopeItem = (bld) => (bld.dataset.scope === 'pdp' ? pdp && pdp.it : List.get(bld.dataset.id));
  const scopeSave = (bld, changed) => { const it = scopeItem(bld); if (it) Rules.normalize(it, changed); if (bld.dataset.scope === 'pdp') pdp.render(); else List.save(); };

  function render() {
    const tpl = document.getElementById('zb-empty-tpl');
    $$('[data-zb-list-items]').forEach((box) => {
      if (!List.items.length) { box.innerHTML = tpl ? tpl.innerHTML : ''; return; }
      box.innerHTML = List.items.map((it) => {
        const ok = Rules.check(it).ok;
        return '<div class="zb-item' + (ok ? '' : ' is-invalid') + '" data-key="' + esc(it.id) + '">' +
          '<a class="zb-item__img" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(it.image) + '" alt="" loading="lazy">' : '') + '</a>' +
          '<div class="zb-item__info"><div class="zb-item__top"><a class="zb-item__title" href="' + esc(it.url) + '">' + esc(it.title) + '</a>' +
          '<button type="button" class="zb-item__remove" data-zb-remove aria-label="' + esc(T.remove) + '">' + xIcon + '</button></div>' +
          (!it.one && it.sizes.length ? '<span class="zb-item__series">' + esc(T.fullSeries) + ': ' + esc(it.sizes.join(' / ')) + '</span>' : '') +
          builderHTML(it, 'list', true) +
          '</div></div>';
      }).join('');
    });
    const valid = List.valid();
    $$('[data-zb-list-foot]').forEach((f) => { f.hidden = !List.items.length; });
    $$('[data-zb-send-list]').forEach((b) => { b.classList.toggle('is-disabled', !valid); b.setAttribute('aria-disabled', !valid); });
    $$('[data-zb-list-error]').forEach((p) => { p.hidden = valid || !List.items.length; p.textContent = T.listInvalid; });
    $$('[data-zb-list-name]').forEach((i) => { if (document.activeElement !== i) i.value = List.meta.name || ''; });
    $$('[data-zb-list-note]').forEach((i) => { if (document.activeElement !== i) i.value = List.meta.note || ''; });
    $$('[data-zb-count]').forEach((c) => { c.textContent = List.count(); c.classList.toggle('is-on', List.count() > 0); });
    $$('[data-zb-add]').forEach((b) => {
      try { const p = JSON.parse(b.dataset.product); const inList = List.has(p.id); b.classList.toggle('is-added', inList); const s = b.querySelector('span'); if (s) s.textContent = inList ? T.inList : T.addShort; } catch (e) { /* noop */ }
    });
    if (pdp && pdp.syncLabel) pdp.syncLabel();
  }
  const bumpCount = () => $$('[data-zb-count]').forEach((c) => { c.classList.remove('is-bump'); void c.offsetWidth; c.classList.add('is-bump'); });
  const shake = (el) => { if (!el) return; el.classList.remove('zb-shake'); void el.offsetWidth; el.classList.add('zb-shake'); };

  /* ---------- toast ---------- */
  let toastTimer;
  function toast(text, img) {
    const el = document.getElementById('zb-toast');
    if (!el) return;
    $('[data-zb-toast-text]', el).textContent = text;
    const im = $('img', el);
    if (img) { im.src = img; im.hidden = false; } else im.hidden = true;
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-on'), 3400);
  }

  /* ---------- overlays (drawer / menu / search) ---------- */
  let lastFocus = null;
  const openLayer = (el, focusSel) => {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('zb-lock');
    const f = focusSel ? $(focusSel, el) : $('button, a, input', el);
    setTimeout(() => f && f.focus({ preventScroll: true }), 60);
  };
  const closeLayer = (el) => {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true');
    if (!$$('.zb-drawer.is-open, .zb-mnav.is-open, .zb-search.is-open').length) document.body.classList.remove('zb-lock');
    if (lastFocus) lastFocus.focus({ preventScroll: true });
  };
  const drawer = () => document.getElementById('zb-drawer');
  const openList = () => { closeLayer(document.getElementById('zb-mnav')); if ($('[data-zb-listpage]')) { $('[data-zb-listpage]').scrollIntoView({ behavior: 'smooth' }); return; } openLayer(drawer(), '[data-zb-close]'); };

  /* ==========================================================================
     GLOBAL CLICK DELEGATION
     ========================================================================== */
  document.addEventListener('click', (e) => {
    const t = e.target;
    let el;
    if ((el = t.closest('[data-zb-open-list]'))) { e.preventDefault(); document.getElementById('zb-toast').classList.remove('is-on'); openList(); return; }
    if ((el = t.closest('[data-zb-close]'))) { closeLayer(drawer()); return; }
    if ((el = t.closest('[data-zb-mnav-open]'))) { openLayer(document.getElementById('zb-mnav')); return; }
    if ((el = t.closest('[data-zb-mnav-close]'))) { closeLayer(document.getElementById('zb-mnav')); return; }
    if ((el = t.closest('[data-zb-search-open]'))) { openLayer(document.getElementById('zb-search'), '[data-zb-search-input]'); return; }
    if ((el = t.closest('[data-zb-search-close]'))) { closeLayer(document.getElementById('zb-search')); return; }

    if ((el = t.closest('[data-zb-wa-general]'))) { if (!openWa(T.waGeneral, el)) e.preventDefault(); return; }
    if ((el = t.closest('[data-zb-wa-product]'))) { if (!openWa(productMessage({ title: el.dataset.title, url: el.dataset.url }), el)) e.preventDefault(); return; }
    if ((el = t.closest('[data-zb-send-list]'))) {
      if (!List.valid()) { e.preventDefault(); shake(el); const bad = $('.zb-item.is-invalid'); bad && bad.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      if (!openWa(List.message(), el)) e.preventDefault();
      return;
    }
    if ((el = t.closest('[data-zb-clear]'))) { List.clear(); return; }

    if ((el = t.closest('[data-zb-add]'))) {
      e.preventDefault();
      try {
        const p = JSON.parse(el.dataset.product);
        const it = List.put(p, List.has(p.id) ? {} : Rules.defaults(p));
        if (Rules.check(it).ok) toast(T.added, it.image);
        else openList(); // let the buyer pick colours right away
      } catch (err) { /* noop */ }
      return;
    }
    if ((el = t.closest('[data-zb-remove]'))) { List.remove(el.closest('[data-key]').dataset.key); return; }

    // order builder controls (product page + list)
    const bld = t.closest('.zb-bld');
    if (bld) {
      const it = scopeItem(bld);
      if (!it) return;
      const row = t.closest('[data-c]');
      if ((el = t.closest('[data-zb-bq]')) && row) { const c = row.dataset.c; it.lines[c] = Math.max(0, (it.lines[c] || 0) + parseInt(el.dataset.zbBq, 10)); if (!it.lines[c] && !it.one) delete it.lines[c]; scopeSave(bld, c); return; }
      if (t.closest('[data-zb-bdel]') && row) { delete it.lines[row.dataset.c]; scopeSave(bld, it.one ? null : row.dataset.c); return; }
      if ((el = t.closest('[data-zb-badd]'))) { it.lines[el.dataset.zbBadd] = 1; scopeSave(bld, it.one ? null : el.dataset.zbBadd); return; }
      if (t.closest('[data-zb-bfix]')) { it.colors.forEach((c) => { if (!(it.lines[c] > 0)) it.lines[c] = 1; }); scopeSave(bld); return; }
    }
    if ((el = t.closest('[data-zb-dd]'))) {
      const item = el.closest('.zb-nav__item');
      const open = !item.classList.contains('is-open');
      $$('.zb-nav__item.is-open').forEach((i) => i.classList.remove('is-open'));
      item.classList.toggle('is-open', open); el.setAttribute('aria-expanded', open);
      return;
    }
    if (!t.closest('.zb-nav__item')) $$('.zb-nav__item.is-open').forEach((i) => { i.classList.remove('is-open'); const b = $('[data-zb-dd]', i); b && b.setAttribute('aria-expanded', 'false'); });
  });

  document.addEventListener('change', (e) => {
    const q = e.target.closest('[data-zb-bq-input]');
    if (!q) return;
    const bld = q.closest('.zb-bld'), row = q.closest('[data-c]');
    const it = bld && scopeItem(bld);
    if (!it || !row) return;
    const v = Math.max(0, parseInt(q.value, 10) || 0);
    if (v || it.one) it.lines[row.dataset.c] = v; else delete it.lines[row.dataset.c];
    scopeSave(bld, row.dataset.c);
  });
  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-zb-list-name]')) { List.meta.name = e.target.value; write(META, List.meta); }
    if (e.target.matches('[data-zb-list-note]')) { List.meta.note = e.target.value; write(META, List.meta); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    ['zb-drawer', 'zb-mnav', 'zb-search'].forEach((id) => closeLayer(document.getElementById(id)));
    const lb = $('[data-zb-lightbox].is-open'); if (lb) lb.classList.remove('is-open', 'is-zoomed');
    const f = $('[data-zb-filters].is-open'); if (f) toggleFilters(false);
  });
  window.addEventListener('storage', (e) => { if (e.key === KEY) { List.items = read(KEY, []); render(); } });

  /* ==========================================================================
     HEADER — shrink, hide on scroll down, toolbar offset
     ========================================================================== */
  const header = $('[data-zb-header]');
  if (header) {
    let lastY = window.scrollY, ticking = false;
    const setTop = () => {
      const hidden = header.classList.contains('is-hidden');
      document.documentElement.style.setProperty('--zb-sticky-top', hidden ? '0px' : header.getBoundingClientRect().height + 'px');
    };
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      const menuOpen = $('.zb-nav__item.is-open') || document.body.classList.contains('zb-lock');
      if (y > 420 && y > lastY + 4 && !menuOpen) header.classList.add('is-hidden');
      else if (y < lastY - 4 || y < 420) header.classList.remove('is-hidden');
      lastY = y; ticking = false; setTop();
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(onScroll); } }, { passive: true });
    window.addEventListener('resize', setTop);
    onScroll();
  }

  /* ---------- predictive search ---------- */
  const sInput = $('[data-zb-search-input]');
  if (sInput) {
    const out = $('[data-zb-search-results]');
    let timer, ctrl;
    sInput.addEventListener('input', () => {
      clearTimeout(timer);
      const q = sInput.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        try {
          if (ctrl) ctrl.abort();
          ctrl = new AbortController();
          const r = await fetch(ROOT + '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,tag', { signal: ctrl.signal });
          const d = await r.json();
          const ps = (d.resources && d.resources.results && d.resources.results.products) || [];
          const allUrl = (cfg.searchUrl || '/search') + '?type=product&q=' + encodeURIComponent(q);
          out.innerHTML = ps.length
            ? ps.map((p) => '<a class="zb-search__hit" href="' + esc(p.url) + '"><img src="' + esc((p.featured_image && p.featured_image.url) || p.image || '') + (String((p.featured_image && p.featured_image.url) || p.image || '').includes('?') ? '&' : '?') + 'width=400" alt="" loading="lazy"><span>' + esc(p.title) + '</span></a>').join('') +
              '<a class="zb-link zb-search__all" href="' + esc(allUrl) + '">' + esc(T.searchAll) + '</a>'
            : '<p class="zb-search__empty">' + esc(T.searchNone) + ' “' + esc(q) + '”</p>';
        } catch (err) { /* aborted */ }
      }, 220);
    });
  }

  /* ==========================================================================
     REVEAL + COUNT-UP
     ========================================================================== */
  const countUp = (el, to) => {
    const target = parseInt(to, 10);
    if (!target || el.dataset.done) return;
    el.dataset.done = '1';
    const start = performance.now(), dur = 1400;
    const fmt = new Intl.NumberFormat(cfg.locale);
    const step = (now) => {
      const p = Math.min(1, (now - start) / dur), e = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt.format(Math.round(target * e));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting) return;
      en.target.classList.add('is-in');
      if (en.target.dataset.zbCountup) countUp(en.target, en.target.dataset.zbCountup);
      io.unobserve(en.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }) : null;
  const observe = (root = document) => $$('.zb-reveal:not(.is-in), [data-zb-countup]', root).forEach((el) => (io ? io.observe(el) : el.classList.add('is-in')));

  /* ==========================================================================
     CARDS — colour swatch previews the variant image
     ========================================================================== */
  document.addEventListener('mouseover', (e) => {
    const sw = e.target.closest('.zb-card .zb-swatch[data-swap]');
    if (!sw) return;
    const card = sw.closest('.zb-card');
    const frame = $('[data-zb-frame]', card);
    const img = $('[data-zb-swap]', card);
    img.hidden = false;
    if (img.getAttribute('src') !== sw.dataset.swap) img.src = sw.dataset.swap;
    frame.classList.add('is-swapped');
    $$('.zb-swatch', card).forEach((s) => s.classList.toggle('is-active', s === sw));
  });
  document.addEventListener('mouseout', (e) => {
    const box = e.target.closest('.zb-card .zb-swatches');
    if (!box || box.contains(e.relatedTarget)) return;
    const card = box.closest('.zb-card');
    $('[data-zb-frame]', card).classList.remove('is-swapped');
    $$('.zb-swatch', card).forEach((s) => s.classList.remove('is-active'));
  });

  /* ==========================================================================
     RAILS
     ========================================================================== */
  function initRails(root = document) {
    $$('[data-zb-rail]', root).forEach((rail) => {
      if (rail.dataset.ready) return; rail.dataset.ready = '1';
      const track = $('[data-zb-rail-track]', rail);
      const section = rail.closest('section') || rail.parentElement;
      const prev = $('[data-zb-rail-prev]', section), next = $('[data-zb-rail-next]', section);
      const bar = $('[data-zb-rail-bar]', rail);
      const sign = RTL ? -1 : 1;
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        const x = Math.abs(track.scrollLeft);
        if (prev) prev.disabled = x < 4;
        if (next) next.disabled = x > max - 4;
        if (bar) bar.style.setProperty('--p', (max > 0 ? Math.max(12, ((x + track.clientWidth) / track.scrollWidth) * 100) : 100) + '%');
      };
      const page = (dir) => track.scrollBy({ left: dir * sign * track.clientWidth * 0.9, behavior: 'smooth' });
      prev && prev.addEventListener('click', () => page(-1));
      next && next.addEventListener('click', () => page(1));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ==========================================================================
     COLLECTION — client-side filters, sort, density, progressive reveal
     ========================================================================== */
  let toggleFilters = () => {};
  function initCollection() {
    const root = $('[data-zb-collection]');
    const grid = root && $('[data-zb-grid]', root);
    if (!grid) return;
    const step = parseInt(root.dataset.page, 10) || 24;
    const panel = $('[data-zb-filters]', root);
    const toggleBtn = $('[data-zb-filter-toggle]', root);
    const none = $('[data-zb-none]', grid);
    const cards = $$('[data-zb-card]', grid).map((el, i) => ({
      el, i,
      type: el.dataset.type || '',
      colors: (el.dataset.colors || '').split('||').filter(Boolean),
      sizes: (el.dataset.sizes || '').split('||').filter(Boolean),
      title: el.dataset.title || '',
      ref: refOf(el.dataset.title),
      date: parseInt(el.dataset.date, 10) || 0
    }));
    const params = new URLSearchParams(location.search);
    const state = {
      type: new Set(params.getAll('type')), color: new Set(params.getAll('color')), size: new Set(params.getAll('size')),
      sort: params.get('sort') || 'featured', shown: step
    };
    const sizeLabel = (v) => { const p = v.split('-'); return p.length > 2 ? p[0].trim() + ' – ' + p[p.length - 1].trim() : v; };

    // Build option lists with counts
    const tally = (field) => {
      const m = new Map();
      cards.forEach((c) => (Array.isArray(c[field]) ? c[field] : [c[field]]).forEach((v) => { if (v) m.set(v, (m.get(v) || 0) + 1); }));
      return [...m.entries()].sort((a, b) => b[1] - a[1]);
    };
    const groups = { type: tally('type'), color: tally('colors'), size: tally('sizes') };
    Object.entries(groups).forEach(([g, opts]) => {
      const box = $('[data-zb-opts="' + g + '"]', root);
      if (!box) return;
      if (opts.length < 2) { box.closest('.zb-fgroup').hidden = true; return; }
      box.innerHTML = opts.map(([v, n]) => {
        if (g === 'color') {
          const hex = colorOf(v) || 'transparent';
          return '<button type="button" class="zb-fcolour" style="--c:' + esc(hex) + '" data-g="color" data-v="' + esc(v) + '" aria-pressed="false"><i></i>' + esc(pretty(v)) + ' <small>' + n + '</small></button>';
        }
        return '<button type="button" class="zb-pill" data-g="' + g + '" data-v="' + esc(v) + '" aria-pressed="false">' + esc(g === 'size' ? sizeLabel(v) : v) + ' <small>' + n + '</small></button>';
      }).join('');
    });
    if (Object.values(groups).every((o) => o.length < 2) && toggleBtn) toggleBtn.hidden = true;

    const sortSel = $('[data-zb-sort]', root);
    if (sortSel) sortSel.value = state.sort;

    const matches = (c) =>
      (!state.type.size || state.type.has(c.type)) &&
      (!state.color.size || c.colors.some((v) => state.color.has(v))) &&
      (!state.size.size || c.sizes.some((v) => state.size.has(v)));

    const sorters = {
      featured: (a, b) => a.i - b.i,
      newest: (a, b) => b.date - a.date || a.i - b.i,
      'ref-asc': (a, b) => a.ref - b.ref,
      'ref-desc': (a, b) => b.ref - a.ref,
      alpha: (a, b) => a.title.localeCompare(b.title, cfg.locale, { numeric: true })
    };

    const apply = (resetShown) => {
      if (resetShown) state.shown = step;
      const list = cards.filter(matches).sort(sorters[state.sort] || sorters.featured);
      const hiddenSet = new Set(cards);
      list.forEach((c, n) => { hiddenSet.delete(c); grid.insertBefore(c.el, none); c.el.hidden = n >= state.shown; c.el.style.setProperty('--d', ((n % 4) * 0.06) + 's'); });
      hiddenSet.forEach((c) => { c.el.hidden = true; });
      if (none) none.hidden = list.length > 0;

      // buttons state
      $$('[data-g]', root).forEach((b) => b.setAttribute('aria-pressed', state[b.dataset.g].has(b.dataset.v)));
      const active = ['type', 'color', 'size'].flatMap((g) => [...state[g]].map((v) => ({ g, v })));
      const badge = $('[data-zb-filter-count]', root);
      if (badge) { badge.textContent = active.length; badge.classList.toggle('is-on', active.length > 0); }
      const act = $('[data-zb-actives]', root);
      if (act) act.innerHTML = active.map(({ g, v }) => '<button type="button" class="zb-active" data-g="' + g + '" data-v="' + esc(v) + '">' + (g === 'color' ? '<i style="--c:' + esc(colorOf(v) || 'transparent') + '"></i>' : '') + esc(g === 'size' ? sizeLabel(v) : pretty(v)) + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6 6 18"/></svg></button>').join('');

      const shown = Math.min(state.shown, list.length);
      const txt = fill(T.showing, { shown, total: list.length });
      const cnt = $('[data-zb-shown]', root); if (cnt) cnt.textContent = txt;
      const more = $('[data-zb-more]', root);
      if (more) {
        more.hidden = list.length === 0;
        $('[data-zb-more-text]', more).textContent = txt;
        $('[data-zb-more-bar]', more).style.setProperty('--p', (list.length ? (shown / list.length) * 100 : 0) + '%');
        $('[data-zb-more-btn]', more).hidden = shown >= list.length;
      }
      // URL state (shareable filtered links)
      const q = new URLSearchParams(location.search);
      ['type', 'color', 'size', 'sort'].forEach((g) => q.delete(g));
      ['type', 'color', 'size'].forEach((g) => state[g].forEach((v) => q.append(g, v)));
      if (state.sort !== 'featured') q.set('sort', state.sort);
      const qs = q.toString();
      history.replaceState(null, '', location.pathname + (qs ? '?' + qs : ''));
      observe(grid);
    };

    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-g]');
      if (b) { const s = state[b.dataset.g]; s.has(b.dataset.v) ? s.delete(b.dataset.v) : s.add(b.dataset.v); apply(true); return; }
      if (e.target.closest('[data-zb-filter-reset]')) { state.type.clear(); state.color.clear(); state.size.clear(); apply(true); return; }
      if (e.target.closest('[data-zb-filter-toggle]')) { toggleFilters(!panel.classList.contains('is-open')); return; }
      if (e.target.closest('[data-zb-filter-close]')) { toggleFilters(false); return; }
      if (e.target.closest('[data-zb-more-btn]')) { state.shown += step; apply(false); return; }
      const d = e.target.closest('[data-zb-cols]');
      if (d) { grid.dataset.cols = d.dataset.zbCols; $$('[data-zb-cols]', root).forEach((x) => x.setAttribute('aria-pressed', x === d)); try { localStorage.setItem('zb-cols', d.dataset.zbCols); } catch (err) { /* noop */ } }
    });
    if (sortSel) sortSel.addEventListener('change', () => { state.sort = sortSel.value; apply(true); });

    toggleFilters = (open) => {
      if (!panel) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      if (open && !mobile) {
        const tb = $('[data-zb-toolbar]', root);
        const top = tb.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--zb-sticky-top')) || 0);
        if (window.scrollY > top + 2) window.scrollTo({ top, behavior: 'smooth' });
      }
      panel.classList.toggle('is-open', open);
      toggleBtn.setAttribute('aria-expanded', open);
      document.body.classList.toggle('zb-lock', open && mobile);
    };

    try { const c = localStorage.getItem('zb-cols'); if (c) { grid.dataset.cols = c; $$('[data-zb-cols]', root).forEach((x) => x.setAttribute('aria-pressed', x.dataset.zbCols === c)); } } catch (err) { /* noop */ }
    apply(false);
  }

  /* ==========================================================================
     PRODUCT PAGE
     ========================================================================== */
  function initProduct() {
    const root = $('[data-zb-pdp]');
    if (!root) return;
    let p;
    try { p = JSON.parse(root.dataset.product); } catch (e) { return; }
    const track = $('[data-zb-track]', root);
    const slides = $$('[data-zb-slide]', root);
    const thumbs = $$('[data-zb-thumb]', root);
    const dots = $$('.zb-gallery__dots span', root);
    const counter = $('[data-zb-counter]', root);
    const sign = RTL ? -1 : 1;
    let index = 0;

    const goTo = (i, smooth = true) => { if (!track) return; track.scrollTo({ left: i * track.clientWidth * sign, behavior: smooth ? 'smooth' : 'auto' }); };
    const sync = () => {
      index = Math.round(Math.abs(track.scrollLeft) / Math.max(1, track.clientWidth));
      thumbs.forEach((t, n) => t.setAttribute('aria-current', n === index));
      dots.forEach((d, n) => d.classList.toggle('is-on', n === index));
      if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;
    };
    track && track.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(parseInt(t.dataset.zbThumb, 10))));

    // lightbox
    const lb = $('[data-zb-lightbox]', root);
    const lbImg = $('[data-zb-lb-img]', root);
    const lbCount = $('[data-zb-lb-count]', root);
    let lbi = 0;
    const lbShow = (i) => {
      if (!slides.length) return;
      lbi = (i + slides.length) % slides.length;
      const img = $('img', slides[lbi]);
      lbImg.src = img.dataset.full || img.currentSrc || img.src;
      lbCount.textContent = (lbi + 1) + ' / ' + slides.length;
      lb.classList.remove('is-zoomed');
    };
    const lbOpen = (i) => { lbShow(i); lb.classList.add('is-open'); document.body.classList.add('zb-lock'); };
    const lbClose = () => { lb.classList.remove('is-open', 'is-zoomed'); document.body.classList.remove('zb-lock'); };
    slides.forEach((s, n) => s.addEventListener('click', () => lbOpen(n)));
    const zoomBtn = $('[data-zb-zoom]', root); zoomBtn && zoomBtn.addEventListener('click', () => lbOpen(index));
    if (lb) {
      $('[data-zb-lb-close]', lb).addEventListener('click', lbClose);
      const pv = $('[data-zb-lb-prev]', lb), nx = $('[data-zb-lb-next]', lb);
      pv && pv.addEventListener('click', () => lbShow(lbi - 1));
      nx && nx.addEventListener('click', () => lbShow(lbi + 1));
      const stage = $('[data-zb-lb-stage]', lb);
      stage.addEventListener('click', (e) => {
        const r = stage.getBoundingClientRect();
        lbImg.style.transformOrigin = ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
        lb.classList.toggle('is-zoomed');
      });
      stage.addEventListener('mousemove', (e) => {
        if (!lb.classList.contains('is-zoomed')) return;
        const r = stage.getBoundingClientRect();
        lbImg.style.transformOrigin = ((e.clientX - r.left) / r.width * 100) + '% ' + ((e.clientY - r.top) / r.height * 100) + '%';
      });
    }

    // order builder: colour swatches toggle colours in/out of the order
    const buyBox = $('.zb-buy', root);
    const box = $('[data-zb-builder]', root);
    const swatches = $$('.zb-colour', root);
    const saved = relang(List.get(p.id), p);
    if (saved) write(KEY, List.items);
    const it = List.fromProduct(p);
    if (saved) it.lines = Object.assign({}, saved.lines);
    else {
      it.lines = Rules.defaults(p);
      const vid = new URLSearchParams(location.search).get('variant');
      const v = vid && p.variants.find((x) => String(x.id) === vid);
      if (v && p.ci >= 0 && !p.one) it.lines[v.o[p.ci]] = 1;
    }
    const variantFor = (c) => p.variants.find((v) => p.ci >= 0 && v.o[p.ci] === c);
    const showColour = (c) => {
      const v = variantFor(c);
      if (!v) return;
      const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState(null, '', u);
      if (v.mid) { const n = slides.findIndex((s) => s.dataset.mediaId === String(v.mid)); if (n >= 0) goTo(n); }
    };
    const addLabel = $('[data-zb-pdp-add-label]', root);
    if (addLabel) addLabel.dataset.orig = addLabel.textContent;
    pdp = {
      it,
      render() {
        if (box) box.innerHTML = builderHTML(it, 'pdp', false);
        swatches.forEach((s) => s.setAttribute('aria-pressed', it.lines[s.dataset.value] > 0));
        const ok = Rules.check(it).ok;
        $$('[data-zb-order], [data-zb-pdp-add]', root).forEach((b) => b.classList.toggle('is-disabled', !ok));
        this.syncLabel();
      },
      syncLabel() {
        if (!addLabel) return;
        const inList = List.get(p.id);
        const same = inList && JSON.stringify(inList.lines) === JSON.stringify(it.lines);
        addLabel.textContent = same ? T.inList : addLabel.dataset.orig;
      }
    };
    swatches.forEach((s) => s.addEventListener('click', () => {
      const c = s.dataset.value;
      if (it.lines[c] > 0 && !p.one) delete it.lines[c];
      else it.lines[c] = it.lines[c] || 1;
      showColour(c);
      pdp.render();
    }));
    pdp.render();

    const currentUrl = () => p.url;
    const guard = (btn) => {
      const res = Rules.check(it);
      if (res.ok) return true;
      shake(box); shake(btn);
      (box || buyBox).scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    };
    $$('[data-zb-order]', root).forEach((a) => a.addEventListener('click', (e) => {
      if (!guard(a)) { e.preventDefault(); return; }
      const text = [T.waHello, T.waProduct, '', '• ' + p.title].concat(styleLines(it, false), [T.waLink + ': ' + absUrl(p.url), '', T.waThanks]).join('\n');
      if (!openWa(text, a)) e.preventDefault();
    }));
    $$('[data-zb-pdp-add]', root).forEach((b) => b.addEventListener('click', () => {
      if (!guard(b)) return;
      const first = Object.keys(it.lines)[0];
      const v = first && variantFor(first);
      List.put(p, it.lines, { replace: true, image: (v && v.img) || p.image });
      toast(T.added, (v && v.img) || p.image);
    }));

    // copy link
    const copy = $('[data-zb-copy]', root);
    copy && copy.addEventListener('click', async () => { try { await navigator.clipboard.writeText(absUrl(currentUrl())); toast(T.copied); } catch (e) { /* noop */ } });

    // sticky bar
    const sticky = $('[data-zb-sticky]', root);
    const buy = $('.zb-buy', root);
    if (sticky && buy && io) {
      new IntersectionObserver(([en]) => {
        const on = !en.isIntersecting && en.boundingClientRect.top < 0;
        sticky.classList.toggle('is-on', on);
        document.body.classList.toggle('zb-has-sticky', on && getComputedStyle(sticky).display !== 'none');
      }).observe(buy);
    }
    window.addEventListener('resize', () => goTo(index, false));
    document.addEventListener('keydown', (e) => {
      if (!lb || !lb.classList.contains('is-open')) return;
      if (e.key === 'ArrowRight') lbShow(lbi + sign);
      if (e.key === 'ArrowLeft') lbShow(lbi - sign);
    });
    lb && lb.addEventListener('click', (e) => { if (e.target === lb) lbClose(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && lb) lbClose(); });
  }

  /* ==========================================================================
     CATALOGUE (for the colour studio + hero stat)
     ========================================================================== */
  let catalogPromise;
  const catalog = () => catalogPromise || (catalogPromise = (async () => {
    const all = [];
    for (let page = 1; page <= 4; page++) {
      const r = await fetch(ROOT + '/products.json?limit=250&page=' + page);
      if (!r.ok) break;
      const d = await r.json();
      all.push(...(d.products || []));
      if (!d.products || d.products.length < 250) break;
    }
    return all;
  })());
  const isColorOpt = (name) => /col|لون|ألوان|الوان/i.test(name || '');
  const sized = (src, w) => (src ? src + (src.includes('?') ? '&' : '?') + 'width=' + w : '');
  const hexToHsl = (hex) => {
    let h = String(hex).replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16) / 255, g = parseInt(h.slice(2, 4), 16) / 255, b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min, s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let hue = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    return [hue * 60, s, l];
  };
  const toHex = (c) => {
    if (/^#/.test(c)) return c;
    const cv = document.createElement('canvas').getContext('2d'); cv.fillStyle = c; return cv.fillStyle;
  };

  async function buildPalette() {
    const products = await catalog();
    const byHex = new Map();
    products.forEach((pr) => {
      const opt = (pr.options || []).find((o) => isColorOpt(o.name));
      if (!opt) return;
      const pos = opt.position;
      opt.values.forEach((val) => {
        const c = colorOf(val);
        if (!c) return;
        const hex = toHex(c).toLowerCase();
        if (!byHex.has(hex)) byHex.set(hex, { hex, names: new Map(), items: [] });
        const g = byHex.get(hex);
        g.names.set(val, (g.names.get(val) || 0) + 1);
        const v = (pr.variants || []).find((x) => x['option' + pos] === val);
        if (!g.items.some((i) => i.p.id === pr.id)) g.items.push({ p: pr, v });
      });
    });
    const list = [...byHex.values()].map((g) => ({ ...g, name: [...g.names.entries()].sort((a, b) => b[1] - a[1])[0][0] }));
    list.forEach((g) => { g.hsl = hexToHsl(g.hex); });
    list.sort((a, b) => {
      const ga = a.hsl[1] < 0.14, gb = b.hsl[1] < 0.14;
      if (ga !== gb) return ga ? 1 : -1;
      if (ga) return b.hsl[2] - a.hsl[2];
      return a.hsl[0] - b.hsl[0];
    });
    return list;
  }

  function initStudio() {
    const root = $('[data-zb-studio]');
    const totals = $$('[data-zb-colour-total]');
    if (!root && !totals.length) return;
    const run = () => buildPalette().then((palette) => {
      totals.forEach((el) => { el.dataset.zbCountup = palette.length; el.removeAttribute('data-done'); if (io) io.observe(el); else el.textContent = palette.length; });
      if (!root) return;
      const limit = parseInt(root.dataset.limit, 10) || 8;
      const chips = $('[data-zb-studio-chips]', root);
      const grid = $('[data-zb-studio-grid]', root);
      const now = $('[data-zb-studio-now]', root);
      chips.innerHTML = palette.map((g, i) => '<button type="button" class="zb-studio__chip" role="radio" aria-checked="false" style="--c:' + g.hex + '" data-i="' + i + '" aria-label="' + esc(pretty(g.name)) + '" title="' + esc(pretty(g.name)) + '"><i>' + g.items.length + '</i></button>').join('');
      const pick = (i) => {
        const g = palette[i];
        $$('.zb-studio__chip', chips).forEach((c) => { const on = c.dataset.i === String(i); c.classList.toggle('is-active', on); c.setAttribute('aria-checked', on); });
        now.style.setProperty('--now', g.hex);
        now.classList.toggle('is-flip');
        $('[data-zb-studio-name]', root).textContent = pretty(g.name);
        $('[data-zb-studio-meta]', root).textContent = fill(T.studioCount, { n: g.items.length });
        grid.innerHTML = g.items.slice(0, limit).map(({ p, v }, n) => {
          const src = (v && v.featured_image && v.featured_image.src) || (p.images[0] && p.images[0].src) || '';
          const url = ROOT + '/products/' + p.handle + (v ? '?variant=' + v.id : '');
          return '<a class="zb-mini" style="--i:' + n + ';--c:' + g.hex + '" href="' + esc(url) + '"><span class="zb-mini__img"><img src="' + esc(sized(src, 500)) + '" alt="' + esc(p.title) + '" loading="lazy"></span><span class="zb-mini__title">' + esc(p.title) + '</span></a>';
        }).join('');
      };
      chips.addEventListener('click', (e) => { const c = e.target.closest('[data-i]'); if (c) pick(parseInt(c.dataset.i, 10)); });
      let best = 0; palette.forEach((g, i) => { if (g.items.length > palette[best].items.length) best = i; });
      if (palette.length) pick(best);
    }).catch(() => { if (root) root.hidden = true; });
    if (root && io) {
      const lazy = new IntersectionObserver(([en]) => { if (en.isIntersecting) { lazy.disconnect(); run(); } }, { rootMargin: '600px 0px' });
      lazy.observe(root);
      if (totals.length) setTimeout(run, 1200);
    } else run();
  }

  /* ==========================================================================
     OPENING HOURS — localised day names/times + live open/closed (Beirut time)
     ========================================================================== */
  function initHours() {
    const weeks = $$('[data-zb-week], [data-zb-hours-list]');
    const status = $$('[data-zb-status]');
    if (!weeks.length && !status.length) return;
    const parse = (s) => {
      const m = String(s || '').match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[-–to]+\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
      if (!m) return null;
      const toMin = (h, mi, ap) => { h = parseInt(h, 10) % 12 + (/pm/i.test(ap || '') ? 12 : 0); if (!ap && parseInt(h, 10) < 7) h += 12; return h * 60 + (parseInt(mi, 10) || 0); };
      return [toMin(m[1], m[2], m[3] || m[6]), toMin(m[4], m[5], m[6])];
    };
    const tf = new Intl.DateTimeFormat(cfg.locale, { hour: 'numeric', minute: '2-digit' });
    const fmtT = (min) => tf.format(new Date(2020, 0, 1, Math.floor(min / 60), min % 60));
    const dayShort = new Intl.DateTimeFormat(cfg.locale, { weekday: 'short' });
    const dayLong = new Intl.DateTimeFormat(cfg.locale, { weekday: 'long' });
    const dayName = (d, long) => (long ? dayLong : dayShort).format(new Date(2023, 0, 1 + d));
    const hours = {}; for (let d = 0; d < 7; d++) hours[d] = parse(cfg.hours[d]);

    const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Beirut', weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(new Date());
    const get = (t) => (parts.find((p) => p.type === t) || {}).value;
    const today = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    const nowMin = (parseInt(get('hour'), 10) % 24) * 60 + parseInt(get('minute'), 10);

    weeks.forEach((w) => {
      const long = w.hasAttribute('data-zb-hours-list');
      $$('[data-day]', w).forEach((el) => {
        const d = parseInt(el.dataset.day, 10);
        const n = $('[data-zb-dayname]', el); if (n) n.textContent = dayName(d, long);
        const h = $('[data-zb-dayhours]', el);
        if (h) {
          if (hours[d]) h.innerHTML = esc(fmtT(hours[d][0])) + (long ? ' – ' : '<br>') + esc(fmtT(hours[d][1]));
          else h.textContent = T.closed;
        }
        el.classList.toggle('is-today', d === today);
        if (long && d === today) el.style.color = '#fff';
      });
      // start the week on Monday in the long list, keep order otherwise
    });

    const todayH = hours[today];
    let open = false, text;
    if (todayH && nowMin >= todayH[0] && nowMin < todayH[1]) { open = true; text = T.openNow + ' · ' + fill(T.closes, { time: fmtT(todayH[1]) }); }
    else {
      for (let i = 0; i < 7; i++) {
        const d = (today + i) % 7, h = hours[d];
        if (!h || (i === 0 && nowMin >= h[0])) continue;
        const label = i === 0 ? T.today : i === 1 ? T.tomorrow : dayName(d, true);
        text = T.closedNow + ' · ' + fill(T.opens, { day: label, time: fmtT(h[0]) });
        break;
      }
      text = text || T.closedNow;
    }
    status.forEach((s) => { s.classList.toggle('is-open', open); $('[data-zb-status-text]', s).textContent = text; });
  }

  /* ==========================================================================
     BOOT
     ========================================================================== */
  const prettify = (root = document) => {
    $$('.zb-colour__tip, [data-zb-opt="color"] [data-zb-opt-value], .zb-swatch .zb-visually-hidden', root).forEach((el) => { el.textContent = pretty(el.textContent); });
    $$('.zb-swatch[title]', root).forEach((el) => { el.title = pretty(el.title); });
  };

  const boot = () => {
    prettify();
    render();
    observe();
    initRails();
    initCollection();
    initProduct();
    initStudio();
    initHours();
    const fab = document.getElementById('zb-fab');
    if (fab && !sessionStorage.getItem('zb-fab-hint')) {
      setTimeout(() => { fab.classList.add('is-hint'); setTimeout(() => fab.classList.remove('is-hint'), 4200); try { sessionStorage.setItem('zb-fab-hint', '1'); } catch (e) { /* noop */ } }, 3500);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  // Theme editor: re-init sections when they are re-rendered
  document.addEventListener('shopify:section:load', (e) => { observe(e.target); initRails(e.target); initStudio(); initHours(); render(); });
  window.ZB = { List, cfg };
})();
